import React, { useEffect, useRef, useState } from "react";
import * as PIXI from "pixi.js";
import { BlurFilter } from "pixi.js";
import gsap from "gsap";
import { observer } from "mobx-react";
import {
  fortuneWheelStore,
  utilsStore,
} from "../../../../../../platform/stores";
import { WheelPrize } from "../../../../../../platform/interfaces/daily-wheel";
import "./Wheel.scss";
import { fortuneWheelApiService } from "../../../../../../platform/api";
import { fortuneWheelService } from "../../../../../../platform/services";
import { getCountDown } from "../../../../../../platform/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

interface WheelProps {
  onSpinComplete?: (prize: WheelPrize) => void;
  onClose?: () => void;
}

function distributeSectors(sectors: number[]): number[] {
  const total = sectors.length;
  const counts: Record<number, number> = {};
  sectors.forEach((s) => {
    counts[s] = (counts[s] || 0) + 1;
  });
  const types = Object.keys(counts)
    .map(Number)
    .sort((a, b) => counts[b] - counts[a]);
  const result = new Array<number>(total).fill(null as any);
  const occupied = new Array(total).fill(false);
  types.forEach((type) => {
    const count = counts[type];
    const step = total / count;
    let position = 0;
    for (let i = 0; i < count; i++) {
      let target = Math.round(position) % total;
      while (occupied[target]) {
        target = (target + 1) % total;
      }
      result[target] = type;
      occupied[target] = true;
      position += step;
    }
  });
  return result;
}

let spinWheel = () => {};

const wheelBorderTexture = PIXI.Texture.from(
  "/assets/daily-wheel/wheel-border.png",
);

const WHEEL_RADIUS = 170;
const WHEEL_CENTER = 220;
// The border art paints a gold cap over the hub; labels must stay outside it
const HUB_CLEARANCE = 48;
const RIM_CLEARANCE = 8;

// Sector fills run from pale gold to deep red, so a single label colour can't
// work for all of them - white on the pale gold sector is barely legible.
// Each label picks its ink from the brightness of the sector underneath it.
function parseRgb(color: string): { r: number; g: number; b: number } {
  const match = color.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return { r: 0, g: 0, b: 0 };
  return { r: +match[1], g: +match[2], b: +match[3] };
}

function isLightSector(color: string): boolean {
  const { r, g, b } = parseRgb(color);
  // Perceived brightness, ITU-R BT.601
  return (r * 0.299 + g * 0.587 + b * 0.114) / 255 > 0.6;
}

// Soft dome shading laid over the sectors so the wheel reads as a lit disc
// instead of a flat pie chart. Built once and reused by every wheel instance.
let shadingTexture: PIXI.Texture | null = null;
function getShadingTexture(): PIXI.Texture {
  if (shadingTexture) return shadingTexture;

  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d")!;
  // Light source sits slightly above centre, matching the pointer at the top
  const gradient = ctx.createRadialGradient(
    size / 2,
    size * 0.36,
    0,
    size / 2,
    size / 2,
    size / 2,
  );
  gradient.addColorStop(0, "rgba(255, 255, 255, 0.20)");
  gradient.addColorStop(0.45, "rgba(255, 255, 255, 0.04)");
  gradient.addColorStop(0.75, "rgba(0, 0, 0, 0.16)");
  gradient.addColorStop(1, "rgba(0, 0, 0, 0.45)");

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();

  shadingTexture = PIXI.Texture.from(canvas);
  return shadingTexture;
}

export const Wheel = observer(({ onSpinComplete, onClose }: WheelProps) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const wheelRef = useRef<PIXI.Container | null>(null);
  const shuffledSectionsRef = useRef<
    Array<{ prize: WheelPrize; color: string }>
  >([]);
  const blurFilterRef = useRef<BlurFilter | null>(null);
  const highlightRef = useRef<PIXI.Graphics | null>(null);
  const borderGlowRef = useRef<PIXI.Sprite | null>(null);

  useEffect(() => {
    if (!canvasRef.current || !fortuneWheelStore.wheelData?.prizes) return;

    const app = new PIXI.Application({
      width: 440,
      height: 440,
      backgroundAlpha: 0,
      antialias: true,
      resolution: 2,
    });

    canvasRef.current.appendChild(app.view as HTMLCanvasElement);
    // Responsive: make canvas 95% of modal width, square, centered
    const canvas = app.view as HTMLCanvasElement;
    canvas.style.width = "95%";
    canvas.style.height = "auto";
    canvas.style.maxWidth = "95%";
    canvas.style.aspectRatio = "1 / 1";
    canvas.style.display = "block";
    canvas.style.margin = "0 auto";
    appRef.current = app;

    // Create wheel container
    const wheel = new PIXI.Container();
    wheel.position.set(WHEEL_CENTER, WHEEL_CENTER);
    app.stage.addChild(wheel as PIXI.DisplayObject);
    wheelRef.current = wheel;

    // Shading lives on the stage rather than inside the wheel so the highlight
    // stays put while the wheel turns - a sheen that spins looks wrong
    const shading = new PIXI.Sprite(getShadingTexture());
    shading.anchor.set(0.5);
    shading.width = WHEEL_RADIUS * 2;
    shading.height = WHEEL_RADIUS * 2;
    shading.position.set(WHEEL_CENTER, WHEEL_CENTER);
    app.stage.addChild(shading as PIXI.DisplayObject);

    // Add static border image to the stage (centered, not rotating)

    const wheelBorder = new PIXI.Sprite(wheelBorderTexture);
    wheelBorder.anchor.set(0.5);
    wheelBorder.width = 450;
    wheelBorder.height = 450;
    wheelBorder.position.set(WHEEL_CENTER, WHEEL_CENTER);
    app.stage.addChild(wheelBorder as PIXI.DisplayObject);

    // A blurred, additively blended copy of the same border art. Fading it in
    // blooms the bulbs already painted into the PNG, so the lights line up with
    // the artwork by construction rather than by hand-placed coordinates.
    const borderGlow = new PIXI.Sprite(wheelBorderTexture);
    borderGlow.anchor.set(0.5);
    borderGlow.width = 450;
    borderGlow.height = 450;
    borderGlow.position.set(WHEEL_CENTER, WHEEL_CENTER);
    borderGlow.blendMode = PIXI.BLEND_MODES.ADD;
    borderGlow.filters = [new BlurFilter(6)];
    borderGlow.alpha = 0;
    app.stage.addChild(borderGlow as PIXI.DisplayObject);
    borderGlowRef.current = borderGlow;

    // Add blur filter
    const blurFilter = new BlurFilter();
    blurFilter.blur = 0;
    wheel.filters = [blurFilter];
    blurFilterRef.current = blurFilter;

    // Draw wheel
    drawWheel();

    return () => {
      if (appRef.current) {
        appRef.current.destroy(true);
      }
    };
  }, [fortuneWheelStore.wheelData]);

  const drawWheel = () => {
    if (!wheelRef.current || !fortuneWheelStore.wheelData?.prizes) return;

    const wheel = wheelRef.current;
    wheel.removeChildren();

    const prizes = fortuneWheelStore.wheelData.prizes;
    const colors = [
      "rgb(71, 9, 68)",
      "rgb(231, 195, 139)",
      "rgb(151, 0, 0)",
      "rgb(18, 121, 14)",
      "rgb(73, 68, 219)",
    ];

    // One entry per sector, keyed by the prize itself rather than by its type.
    // Spreading by type only separates "no prize" from "free spins" - every
    // free spin prize shares type 2 - which leaves all the 10s on one side of
    // the wheel and all the 20s on the other. Keying by prize interleaves them.
    const flatPrizeKeys: number[] = [];
    prizes.forEach((prize, prizeIndex) => {
      for (let i = 0; i < prize.sectionCount; i++) {
        flatPrizeKeys.push(prizeIndex);
      }
    });

    const distributedKeys = distributeSectors(flatPrizeKeys);
    const sections: Array<{ prize: WheelPrize; color: string }> =
      distributedKeys.map((prizeIndex) => ({
        prize: prizes[prizeIndex],
        // Colour per prize, not per type, so neighbouring sectors stay distinguishable
        color: colors[prizeIndex % colors.length],
      }));
    shuffledSectionsRef.current = [...sections];

    const totalSections = sections.length;
    const anglePerSection = (2 * Math.PI) / totalSections;
    const radius = WHEEL_RADIUS;

    highlightRef.current = null;

    sections.forEach((sectionData, index) => {
      const currentAngle = index * anglePerSection;
      const section = new PIXI.Graphics();

      section.beginFill(sectionData.color);
      section.moveTo(0, 0);
      section.arc(0, 0, radius, currentAngle, currentAngle + anglePerSection);
      section.lineTo(0, 0);
      section.endFill();

      // Thin gold spokes read as inlay; the old heavy black outline just
      // chopped the disc into disconnected slices
      section.lineStyle(1.5, 0xfed666, 0.5);
      section.moveTo(0, 0);
      section.arc(0, 0, radius, currentAngle, currentAngle + anglePerSection);
      section.lineTo(0, 0);

      wheel.addChild(section as PIXI.DisplayObject);
    });

    // Winner spotlight, drawn above the sectors but kept invisible until a prize lands
    const highlight = new PIXI.Graphics();
    highlight.alpha = 0;
    wheel.addChild(highlight as PIXI.DisplayObject);
    highlightRef.current = highlight;

    // Labels go on last so nothing is drawn over them
    sections.forEach((sectionData, index) => {
      const isPrize = sectionData.prize.type === 2;
      const value = isPrize ? `${sectionData.prize.freeSpinQuantity}` : "NO";
      const caption = isPrize ? "SPINS" : "PRIZE";

      // Ink chosen against the sector fill rather than fixed white
      const light = isLightSector(sectionData.color);
      const valueFill = light ? 0x2a1a00 : 0xffffff;
      const captionFill = light ? 0x5a3d05 : 0xfed666;
      const outline = light ? 0xfff4d6 : 0x000000;

      // Narrower sectors leave less room across the label, so step the type down
      const typeScale =
        totalSections > 12 ? 0.7 : totalSections > 8 ? 0.85 : 1;

      const valueText = new PIXI.Text(value, {
        fontFamily: '"Bebas Neue", Arial, sans-serif',
        fontSize: Math.round(34 * typeScale),
        fill: valueFill,
        stroke: outline,
        strokeThickness: 5,
        align: "center",
      });

      const captionText = new PIXI.Text(caption, {
        fontFamily: '"Bebas Neue", Arial, sans-serif',
        fontSize: Math.round(20 * typeScale),
        fill: captionFill,
        stroke: outline,
        strokeThickness: 4,
        letterSpacing: 1.5,
        align: "center",
      });

      // Both parts sit on one line running along the radius, so the label
      // reads outwards from the hub the way a real prize wheel is printed.
      // Rotating the container is what tilts the text with its sector.
      const gap = 8;
      const totalWidth = valueText.width + gap + captionText.width;

      valueText.anchor.set(0, 0.5);
      valueText.position.set(-totalWidth / 2, 0);

      captionText.anchor.set(0, 0.5);
      captionText.position.set(-totalWidth / 2 + valueText.width + gap, 0);

      const label = new PIXI.Container();
      label.addChild(valueText as PIXI.DisplayObject);
      label.addChild(captionText as PIXI.DisplayObject);

      const labelAngle = index * anglePerSection + anglePerSection / 2;
      // Centre the line in the band of the sector it has room to occupy
      const labelRadius = radius * 0.58;

      // Shrink a long label (a four figure prize) so it can't run under the
      // gold hub cap of the border art or off the rim
      const maxLabelWidth =
        2 * Math.min(labelRadius - HUB_CLEARANCE, radius - RIM_CLEARANCE - labelRadius);
      if (totalWidth > maxLabelWidth) {
        label.scale.set(maxLabelWidth / totalWidth);
      }

      label.position.set(
        Math.cos(labelAngle) * labelRadius,
        Math.sin(labelAngle) * labelRadius,
      );
      label.rotation = labelAngle;

      wheel.addChild(label as PIXI.DisplayObject);
    });
  };

  // Flash the sector that won so the result is obvious on the wheel itself
  const highlightWinningSection = (index: number) => {
    const highlight = highlightRef.current;
    const totalSections = shuffledSectionsRef.current.length;
    if (!highlight || !totalSections) return;

    const anglePerSection = (2 * Math.PI) / totalSections;
    const startAngle = index * anglePerSection;

    highlight.clear();
    highlight.beginFill(0xffffff, 0.25);
    highlight.moveTo(0, 0);
    highlight.arc(0, 0, WHEEL_RADIUS, startAngle, startAngle + anglePerSection);
    highlight.lineTo(0, 0);
    highlight.endFill();

    highlight.lineStyle(3, 0xfed666, 1);
    highlight.moveTo(0, 0);
    highlight.arc(0, 0, WHEEL_RADIUS, startAngle, startAngle + anglePerSection);
    highlight.lineTo(0, 0);

    gsap.fromTo(
      highlight,
      { alpha: 0 },
      { alpha: 1, duration: 0.3, repeat: 5, yoyo: true },
    );
  };

  // Blink the bulbs around the border to celebrate a win
  const flashBorderLights = () => {
    const glow = borderGlowRef.current;
    if (!glow) return;

    gsap.killTweensOf(glow);
    glow.alpha = 0;
    gsap.to(glow, {
      alpha: 0.95,
      duration: 0.18,
      repeat: 7,
      yoyo: true,
      ease: "power2.inOut",
      onComplete: () => {
        glow.alpha = 0;
      },
    });
  };

  // Matches the win test the result panel uses, so the lights only fire when
  // the modal is actually going to say "Congratulations"
  function isWinningPrize(prize: WheelPrize) {
    const hasFreeSpins = prize.freeSpinQuantity > 0;
    const hasFreeBetAmount = prize.freeSpinAmount > 0 && !!prize.currency;
    return !prize.isDefault && (hasFreeSpins || hasFreeBetAmount);
  }

  function isPrizeMatch(prizeA: WheelPrize, prizeB: WheelPrize) {
    return (
      prizeA.type === prizeB.type &&
      prizeA.freeSpinQuantity === prizeB.freeSpinQuantity &&
      prizeA.freeSpinAmount === prizeB.freeSpinAmount
    );
  }

  // The pointer drawn on the wheel border sits at the top of the wheel
  const POINTER_ANGLE = -Math.PI / 2;

  // Find every sector that represents the prize returned by the API.
  // Match by id first, then by prize value, and only fall back to the type:
  // several prizes share the same type (free spins with different quantities),
  // so matching by type alone stops on the wrong sector.
  function getSectionIndexesForPrize(prize: WheelPrize): number[] {
    const sections = shuffledSectionsRef.current;
    const collect = (predicate: (p: WheelPrize) => boolean) =>
      sections.reduce<number[]>(
        (acc, s, i) => (predicate(s.prize) ? [...acc, i] : acc),
        [],
      );

    const byId = collect((p) => p.id === prize.id);
    if (byId.length) return byId;

    const byValue = collect((p) => isPrizeMatch(p, prize));
    if (byValue.length) return byValue;

    return collect((p) => p.type === prize.type);
  }

  // Winning sector plus the absolute rotation that brings its middle under the pointer
  function getLandingForPrize(prize: WheelPrize): {
    index: number;
    rotation: number;
  } {
    const totalSections = shuffledSectionsRef.current.length;
    const indexes = totalSections ? getSectionIndexesForPrize(prize) : [];
    if (!indexes.length) return { index: -1, rotation: 0 };

    // Pick randomly between identical sectors so the wheel doesn't always stop in the same spot
    const index = indexes[Math.floor(Math.random() * indexes.length)];
    const anglePerSection = (2 * Math.PI) / totalSections;
    const sectionCenter = index * anglePerSection + anglePerSection / 2;
    const target = POINTER_ANGLE - sectionCenter;

    return {
      index,
      rotation: ((target % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI),
    };
  }

  spinWheel = async () => {
    const wheel = wheelRef.current;
    const sections = shuffledSectionsRef.current;
    if (wheel === null) return;

    // Get the first unused spin ID
    const unusedSpins = fortuneWheelStore.unusedSpins;
    if (unusedSpins.length === 0) {
      console.error("No unused spins available");
      return;
    }
    const spinId = unusedSpins[0].id;

    const blurFilter = blurFilterRef.current;

    fortuneWheelStore.setIsSpinning(true);

    if (highlightRef.current) {
      gsap.killTweensOf(highlightRef.current);
      highlightRef.current.alpha = 0;
    }
    if (borderGlowRef.current) {
      gsap.killTweensOf(borderGlowRef.current);
      borderGlowRef.current.alpha = 0;
    }

    gsap.to(wheel, {
      rotation: "+=" + Math.PI * 2,
      ease: "none",
      repeat: -1,
    });

    // Motion blur while the wheel is at full speed
    if (blurFilter) gsap.to(blurFilter, { blur: 5, duration: 0.5 });

    try {
      const result = await fortuneWheelApiService.spinWheel(
        utilsStore.gameId!,
        spinId,
      );
      gsap.killTweensOf(wheel);
      wheel.rotation =
        ((wheel.rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const landing = getLandingForPrize(result.prize);
      const rotation = 4 * Math.PI - wheel.rotation + landing.rotation;

      // Let the blur fall away over the tail of the spin as the wheel slows
      if (blurFilter) {
        gsap.to(blurFilter, {
          blur: 0,
          duration: rotation / 3,
          ease: "power2.out",
        });
      }

      await gsap.to(wheel, {
        rotation: "+=" + rotation,
        duration: rotation / 3,
        ease: "back.out(1.3)",
      });

      // Small settle pop, then flash the sector that actually won
      gsap.fromTo(
        wheel.scale,
        { x: 1, y: 1 },
        { x: 1.03, y: 1.03, duration: 0.15, yoyo: true, repeat: 1 },
      );
      if (landing.index >= 0) highlightWinningSection(landing.index);
      if (isWinningPrize(result.prize)) flashBorderLights();

      // Remove the used spin from the array immediately
      const updatedSpins = unusedSpins.filter((spin) => spin.id !== spinId);
      fortuneWheelStore.setUnusedSpins(updatedSpins);

      // Call onSpinComplete callback to show prize result
      if (onSpinComplete) {
        onSpinComplete(result.prize);
      }

      // Close modal after showing prize for a few seconds (don't show countdown)
      // Use onClose callback if available (for animation), otherwise directly close
      gsap.delayedCall(3, () => {
        if (onClose) {
          onClose();
        } else {
          fortuneWheelStore.setDailyWheelModalIsOpen(false);
        }
      });

      // Refresh last spin date from API in background (for countdown when modal is reopened)
      if (fortuneWheelStore.wheelData) {
        // Call to get correct lastSpinDate from server (won't be visible until modal is reopened)
        fortuneWheelService.refreshLastSpinDate(
          fortuneWheelStore.wheelData!.id,
          spinId,
        );
      }
    } catch (e) {
      gsap.killTweensOf(wheel);
      wheel.rotation =
        ((wheel.rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      if (blurFilter) {
        gsap.killTweensOf(blurFilter);
        blurFilter.blur = 0;
      }
      fortuneWheelStore.setIsSpinning(false);
      console.error("Error spinning wheel:", e);
      return;
    }

    fortuneWheelStore.setIsSpinning(false);
  };

  return (
    <div className="wheel-container">
      <div ref={canvasRef} className="wheel-canvas" />
      <WheelBtn />
    </div>
  );
});

const WheelBtn = observer(() => {
  const hasUnusedSpins = fortuneWheelStore.unusedSpins.length > 0;
  const isSpinning = fortuneWheelStore.isSpinning;

  // Show spin button only if we have unused spins
  if (!hasUnusedSpins) {
    return null;
  }

  return (
    <button
      className={`spin-button ${isSpinning ? "disabled" : ""}`}
      onClick={isSpinning ? undefined : spinWheel}
      disabled={isSpinning}
    >
      SPIN
    </button>
  );
});

const CountDownBtn = observer(() => {
  const [countDownText, setCountDownText] = useState("");
  const startCountDown = () => {
    if (!fortuneWheelStore.lastSpinDate || !fortuneWheelStore.wheelData) {
      return;
    }
    requestAnimationFrame(startCountDown);
    // Use configuration.participationPeriodSeconds (preferred) or fallback to root level
    const participationPeriod =
      fortuneWheelStore.wheelData.configuration?.participationPeriodSeconds ||
      fortuneWheelStore.wheelData.participationPeriodSeconds ||
      86400;
    const newCountDownText = getCountDown(
      fortuneWheelStore.lastSpinDate,
      participationPeriod,
    );
    if (newCountDownText && newCountDownText !== countDownText)
      setCountDownText(newCountDownText);
    else if (!newCountDownText) fortuneWheelStore.setLastSpinDate(null);
  };
  startCountDown();

  return <button className="spin-button disabled">{countDownText}</button>;
});
